// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useRef, useState } from "react";
import { Button, Modal, Select, Switch } from "antd";
import { AudioMutedOutlined, AudioOutlined, VideoCameraAddOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { enumerateInputs, micLabel, cameraLabel } from "../devices";
import { BackgroundEffect } from "../bgeffect";

// LobbySettings are the green-room choices (persisted in localStorage so the NEXT join is
// pre-filled — dial them in once and join fast thereafter).
export interface LobbySettings {
  muted: boolean;
  cameraOff: boolean;
  blur: boolean;
  micId?: string;
  cameraId?: string;
}

// CallLobby is the "green room": before joining a GROUP call, preview your camera (with the
// blur effect applied live), pick mic/camera, and pre-set mute/camera-off — so you join with
// the right settings instead of surprising the room. On Join it hands its preview stream to
// the call (no second getUserMedia / camera blink).
export function CallLobby({
  open,
  channelName,
  persisted,
  onJoin,
  onCancel,
}: {
  readonly open: boolean;
  readonly channelName: string;
  readonly persisted: LobbySettings;
  readonly onJoin: (settings: LobbySettings, stream: MediaStream | null) => void;
  readonly onCancel: () => void;
}) {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [muted, setMuted] = useState(persisted.muted);
  const [cameraOff, setCameraOff] = useState(persisted.cameraOff);
  const [blur, setBlur] = useState(persisted.blur);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [micId, setMicId] = useState(persisted.micId);
  const [cameraId, setCameraId] = useState(persisted.cameraId);
  const [denied, setDenied] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const effectRef = useRef<BackgroundEffect | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Acquire / refresh the preview stream when the modal opens or the chosen device changes.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    void (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          audio: micId ? { deviceId: { exact: micId } } : true,
          // Camera off ⇒ acquire audio only (no camera light, and joining is audio-first); the
          // video track's PRESENCE is what "camera on" means to the call.
          video: cameraOff ? false : cameraId ? { deviceId: { exact: cameraId } } : true,
        });
        if (!alive) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current?.getTracks().forEach((t) => t.stop()); // drop the previous device's stream
        streamRef.current = s;
        setStream(s);
        setDenied(false);
        const inputs = await enumerateInputs(); // labels are available now that permission is granted
        if (alive) {
          setMics(inputs.mics);
          setCameras(inputs.cameras);
        }
      } catch {
        if (alive) setDenied(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, micId, cameraId, cameraOff]);

  // Show the preview — blurred (live) when the effect is on, otherwise the raw camera.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream) return;
    let cancelled = false;
    void (async () => {
      effectRef.current?.stop();
      effectRef.current = null;
      if (blur && !cameraOff) {
        try {
          const fx = new BackgroundEffect();
          const out = await fx.start(stream, "blur");
          if (cancelled) {
            fx.stop();
            return;
          }
          effectRef.current = fx;
          el.srcObject = out;
        } catch {
          el.srcObject = stream; // blur unavailable on this device → raw preview
        }
      } else {
        el.srcObject = stream;
      }
      void el.play().catch(() => undefined);
    })();
    return () => {
      cancelled = true;
    };
  }, [stream, blur, cameraOff]);

  // Tear down the preview (stop the effect + release the camera/mic).
  const teardown = () => {
    effectRef.current?.stop();
    effectRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  };

  const cancel = () => {
    teardown();
    onCancel();
  };
  const join = () => {
    const s = streamRef.current;
    effectRef.current?.stop(); // the call applies its OWN blur — hand over the RAW stream
    effectRef.current = null;
    streamRef.current = null; // hand ownership to the call (don't stop its tracks)
    setStream(null);
    onJoin({ muted, cameraOff, blur, micId, cameraId }, s);
  };

  return (
    <Modal open={open} title={`Join ${channelName}`} onCancel={cancel} maskClosable={false} footer={null} width={460} className="lobby-modal">
      <div className="lobby-preview">
        {stream && !cameraOff ? (
          <video ref={videoRef} autoPlay playsInline muted className="lobby-video call-self" />
        ) : (
          <div className="lobby-camoff">
            <VideoCameraAddOutlined />
            <span>{denied ? "No camera / mic access" : "Camera off"}</span>
          </div>
        )}
      </div>
      <div className="lobby-row">
        <Button shape="circle" size="large" type={muted ? "primary" : "default"} danger={muted} icon={muted ? <AudioMutedOutlined /> : <AudioOutlined />} onClick={() => setMuted((m) => !m)} title={muted ? "Will join muted" : "Mic on"} />
        <Button shape="circle" size="large" type={cameraOff ? "primary" : "default"} danger={cameraOff} icon={cameraOff ? <VideoCameraAddOutlined /> : <VideoCameraOutlined />} onClick={() => setCameraOff((c) => !c)} title={cameraOff ? "Will join with camera off" : "Camera on"} />
      </div>
      <div className="lobby-settings">
        <label className="lobby-field">
          <span>Microphone</span>
          <Select size="small" value={micId} placeholder="Default" disabled={!mics.length} onChange={setMicId} options={mics.map((d, i) => ({ value: d.deviceId, label: micLabel(d, i) }))} />
        </label>
        <label className="lobby-field">
          <span>Camera</span>
          <Select size="small" value={cameraId} placeholder="Default" disabled={!cameras.length} onChange={setCameraId} options={cameras.map((d, i) => ({ value: d.deviceId, label: cameraLabel(d, i) }))} />
        </label>
        <label className="lobby-field lobby-toggle">
          <span>Blur background</span>
          <Switch checked={blur} onChange={setBlur} disabled={cameraOff} />
        </label>
      </div>
      <div className="lobby-actions">
        <Button onClick={cancel}>Cancel</Button>
        <Button type="primary" onClick={join}>
          Join now
        </Button>
      </div>
    </Modal>
  );
}
